// src/app/firebase/firebase.service.ts
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging'; // Correct imports from @angular/fire/messaging
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environment.dev';
import { NotificationService } from '../mainServices/notification-service';
import { SessionService } from '../mainServices/session';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private messaging = inject(Messaging);
  currentMessage = new BehaviorSubject<any>(null); // Use <any> for payload type
  dataService = inject(NotificationService);
  sessionService = inject(SessionService);
  readonly count: WritableSignal<number> = signal(0);

  constructor() {
    this.listen(); // Start listening for messages immediately
  }

  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications.');
      return;
    }

    try {
      // getToken correctly uses the injected `this.messaging` instance
      const token = await getToken(this.messaging, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: await this.registerServiceWorker(),
      });

      if (token) {
        console.log('FCM device token generated:', token);
        const data = {
          fcmToken: token,
        };
        this.sendFCMTokenToBackEnd(data).subscribe({
          next: (res: any) => {
            console.log('FCM token sent to backend successfully:', res);
            if (res?.sessionToken) {
              this.sessionService.saveSession(res, res.sessionToken);
            }
          },
          error: (err) => {
            console.error('Failed to send FCM token to backend:', err);
          },
        });
      } else {
        console.log(
          'No FCM device token available. Notification permission not granted or denied.',
        );
      }
    } catch (err) {
      console.error('Permission denied or error during token request:', err);
      if ((err as any).code === 'messaging/permission-blocked') {
        alert('Notifications are blocked by the user. Please unblock them in browser settings.');
      }
    }
  }

  /**
   * The worker can't read environment.ts, so the active project's config rides
   * along in the registration URL - see public/firebase-messaging-sw.js. Left
   * to itself the SDK would register that file bare, and the worker would fall
   * back to whichever project happened to be hardcoded in it.
   */
  private async registerServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
    if (!('serviceWorker' in navigator)) {
      console.warn('This browser does not support service workers; push will not be delivered.');
      return undefined;
    }

    // vapidKey is passed to getToken directly and is of no use to the worker.
    const { vapidKey, ...config } = environment.firebase;
    const params = new URLSearchParams(config);

    return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params}`, { scope: '/' });
  }

  listen() {
    // onMessage correctly uses the injected `this.messaging` instance
    onMessage(this.messaging, (payload) => {
      console.log('Foreground message received: ', payload);
      this.currentMessage.next(payload);
      this.count.update((c) => c + 1);
    });
  }

  sendFCMTokenToBackEnd(tokenData: { fcmToken: string }): Observable<any> {
    return this.dataService.saveFcmToken(tokenData);
  }
}
